import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { BasketUnit, OrderCreate, Unit } from '../types'
import { AuthContext } from './auth_context'
import { basketRepository } from '../repositories/basket_repository'
import { AxiosError } from 'axios'
import { ErrorsContext } from './errors_context'
import { stripeCheckoutSessionsRepository } from '../repositories/stripe_checkout_sessions_repository'
import { getLocalReferral, setLocalReferral } from '../lib/local_storage'
import { i18n } from '../components/i18n'

// type OrderCreate = Readonly<{
//   address?: Address
//   regionShippingService?: RegionShippingService
//   listings?: Listing[]
//   addressField?: SelectField
//   regionShippingServiceField?: SelectField
//   returnPath?: string
//   feedbacks?: Feedback[]
//   referral?: string
// }>

const BasketContext = createContext<{
  basketUnits: BasketUnit[]
  addUnitToBasket?: (unit: Unit, overwrite: boolean, quantity: number, mode: string) => boolean | Promise<boolean>
  removeUnitFromBasket?: (unit: Unit) => boolean | Promise<boolean>
  clearBasket?: () => void
  closeOrderError?: () => void
  showBasket: boolean
  setShowBasket?: (showBasket: boolean) => void
  showOrderAddress: boolean
  setShowOrderAddress?: (show: boolean) => void
  setPreparedOrder?: (order: OrderCreate) => void
  showOrderLoading: boolean
  setShowOrderLoading?: (show: boolean) => void
  showOrderError?: string
  showOrderComplete: boolean
  basketAddedMessage?: Unit
  referral: string
  setReferral?: (value: string) => void
}>({
      basketUnits: [],
      showBasket: false,
      showOrderAddress: false,
      showOrderLoading: false,
      showOrderComplete: false,
      referral: getLocalReferral(),
    })

const basketLocalStorageKey = 'basket'

const BasketProvider = ({ children }) => {
  const { addError } = useContext(ErrorsContext)
  const { isAuthenticated, currentUser } = useContext(AuthContext)

  const [showBasket, _setShowBasket] = useState<boolean>(false)
  const [order, _setPreparedOrder] = useState<OrderCreate|undefined>()
  const [showOrderAddress, _setShowOrderAddress] = useState<boolean>(false)
  const [showOrderLoading, _setShowOrderLoading] = useState<boolean>(false)
  const [showOrderError, _setShowOrderError] = useState<string|undefined>()
  const [showOrderComplete, _setShowOrderComplete] = useState<boolean>(false)
  const [basketAddedMessage, _setBasketAddedMessage] = useState<Unit|undefined>()

  const [_basketInitiated, _setBasketInitiated] = useState<boolean>(false)

  const _localStorageItems = useCallback(() => {
    const items = window.localStorage.getItem(basketLocalStorageKey)

    return items !== null
      ? JSON.parse(items)
      : []
  }, [])

  const [basketUnits, setBasketUnits] = useState<BasketUnit[]>(() => {
    if (!isAuthenticated) {
      return _localStorageItems()
    }

    return []
  })

  const clearBasket = useCallback(() => {
    setBasketUnits([])

    // sync with local storage
    window.localStorage.removeItem(basketLocalStorageKey)

    // sync with database
    if (isAuthenticated) {
      basketRepository
        .delete_all()
        .catch((err: AxiosError) => {
          console.error(err)
        })
    }
  }, [isAuthenticated])

  const closeOrderError = useCallback(() => {
    _setShowOrderError(undefined)
  }, [])

  const setShowBasket = useCallback((showBasket) => {
    _setShowBasket(showBasket)
    _setShowOrderComplete(false)
  }, [])

  useEffect(() => {
    if (!showBasket) _setBasketAddedMessage(undefined)
  }, [showBasket])

  const setShowOrderAddress = useCallback((show) => {
    _setShowOrderAddress(show)
  }, [])

  const setPreparedOrder = useCallback((order) => {
    _setPreparedOrder(order)
  }, [])

  const setShowOrderLoading = useCallback((show) => {
    _setShowOrderLoading(show)
  }, [])

  const addressRequired = useMemo(() => {
    if (!basketUnits) return true

    // determine if a basket item requires an address
    return basketUnits.some(basketUnit => basketUnit.unit.items?.some(item => item.type !== 'DigitalDownload'))
  }, [basketUnits])

  const createOrder = useCallback(async () => {
    if (order === undefined) {
      alert(i18n.t('checkout.errors.generic'))
      return
    }

    if (addressRequired && (!order.regionShippingService || !order.address)) {
      alert(i18n.t('checkout.errors.select_address'))
      return
    }

    const { loadStripe } = await import('@stripe/stripe-js')
    const stripe = await loadStripe(String(process.env.REACT_APP_STRIPE_PUBLIC_KEY))

    if (stripe === null) {
      alert(i18n.t('checkout.errors.stripe_load_failed'))
      return
    }

    const mode = order.mode ?? 'payment'

    const sessionData = await stripeCheckoutSessionsRepository.create({
      addressId: addressRequired ? Number(order.address?.id) : undefined,
      regionShippingServiceId: addressRequired ? order.regionShippingService?.id : undefined,
      returnPath: order.returnPath ?? '',
      referral: order.referral ?? '',
      points: order.points ?? 0,
      mode,
    })
      .catch((err) => {
        addError?.(err)
        _setShowOrderLoading(false)
        _setShowOrderError('The item is no longer available to purchase.')
      })

    if (sessionData) {
      const sessionId = sessionData.sessionId
      const clientSecret = sessionData.clientSecret

      if (mode === 'subscription') {
        // move to the subscription payment page
        if (!clientSecret) {
          alert(i18n.t('checkout.errors.generic'))
          return
        }

        // clear basket
        setBasketUnits([])

        window.location.href = `/subscriptions/${sessionId}`
        return
      } else if (mode === 'payment') {
        if (!sessionId) {
          alert(i18n.t('checkout.errors.generic'))
          return
        }

        stripe.redirectToCheckout({
          // Make the id field from the Checkout Session creation API response
          // available to this file, so you can provide it as argument here
          // instead of the {{CHECKOUT_SESSION_ID}} placeholder.
          sessionId: sessionId
        }).then((result) => {
          alert(result.error.message)
          // If `redirectToCheckout` fails due to a browser or network
          // error, display the localized error message to your customer
          // using `result.error.message`.
          _setShowOrderLoading(false)
        })
      } else {
        _setShowOrderLoading(false)
        _setShowOrderComplete(true)
        setBasketUnits([])
      }
    }
  }, [addError, addressRequired, order])

  useEffect(() => {
    if (order) {
      // move to Stripe
      _setShowOrderLoading(true)
      createOrder()
    }
  }, [createOrder, order])

  const currentBasketUnitsSeller = useMemo(() => basketUnits.length > 0 ? basketUnits[0].unit.userId : undefined, [basketUnits])

  const _addToDb = useCallback((unitId, quantity, mode) => {
    return basketRepository
      .create({
        unitId,
        quantity,
        mode,
      })
      .then(() => {
        return true
      })
      .catch((err: AxiosError) => {
        alert(i18n.t('checkout.errors.generic'))
        console.error(err)
        return false
      })
  }, [addError])

  const _removeFromDb = useCallback((unitId) => {
    return basketRepository
      .delete(unitId)
      .then(() => {
        return true
      })
      .catch((err: AxiosError) => {
        return false
      })
  }, [addError])

  const addUnitToBasket = useCallback((unit: Unit, overwrite: boolean, quantity: number, mode: string) => {
    const inBasket = basketUnits.find(basketUnit => basketUnit.unit.id === unit.id)

    const quantityNew = mode === 'subscription' ? 1 : quantity

    if (currentBasketUnitsSeller && unit.userId !== currentBasketUnitsSeller && !overwrite) {
      // For phase 1, a user can only add one seller's items to their basket
      return false
    }

    // if the basket has items with different modes, don't allow adding
    if (basketUnits.length > 0 && basketUnits.some(basketUnit => basketUnit.mode !== mode) && !overwrite) {
      return false
    }

    const basketUnit = {
      unit,
      quantity: quantityNew,
      mode,
    }

    const applyLocalAdd = () => {
      setBasketUnits(current => [
        ...current.filter(bu => bu.unit.id !== unit.id),
        basketUnit,
      ])
      _setBasketAddedMessage(unit)
    }

    // toggle mode if the unit is already in the basket
    if (inBasket && inBasket.mode !== mode && !overwrite) {
      if (isAuthenticated) {
        return _addToDb(unit.id, quantityNew, mode).then((ok) => {
          if (ok) applyLocalAdd()
          return ok
        })
      }

      applyLocalAdd()
      return true
    } else if (overwrite) {
      if (isAuthenticated) {
        const previous = basketUnits

        return basketRepository
          .delete_all()
          .then(() => _addToDb(unit.id, quantityNew, mode))
          .then(async (ok) => {
            if (ok) {
              setBasketUnits([basketUnit])
              _setBasketAddedMessage(unit)
              return true
            }

            await Promise.all(previous.map((bu) => {
              const unitId = bu.unit.id
              if (unitId === undefined) return Promise.resolve()

              return basketRepository.create({
                unitId,
                quantity: bu.quantity,
                mode: bu.mode as 'payment' | 'subscription',
              }).catch(() => undefined)
            }))

            return false
          })
          .catch((err: AxiosError) => {
            console.error(err)
            alert(i18n.t('checkout.errors.generic'))
            return false
          })
      }

      setBasketUnits([basketUnit])
      window.localStorage.setItem(basketLocalStorageKey, JSON.stringify([basketUnit]))
      _setBasketAddedMessage(unit)
      return true
    } else if (!inBasket || inBasket.quantity !== quantityNew) {
      if (isAuthenticated) {
        return _addToDb(unit.id, quantityNew, mode).then((ok) => {
          if (ok) {
            setBasketUnits(current => [
              ...current.filter(bu => bu.unit.id !== unit.id),
              {
                unit,
                quantity: quantityNew,
                mode,
              },
            ])
            _setBasketAddedMessage(unit)
          }
          return ok
        })
      }

      applyLocalAdd()
      return true
    }

    return true
  }, [_addToDb, basketUnits, currentBasketUnitsSeller, isAuthenticated])

  const removeUnitFromBasket = useCallback((unit: Unit) => {
    const inBasket = basketUnits.find(basketUnit => basketUnit.unit.id === unit.id)

    if (!inBasket) {
      _setBasketAddedMessage(undefined)
      return true
    }

    if (isAuthenticated) {
      return _removeFromDb(unit.id).then((ok) => {
        if (ok) {
          setBasketUnits(current => current.filter(bu => bu.unit.id !== unit.id))
          _setBasketAddedMessage(undefined)
        } else {
          alert(i18n.t('checkout.errors.generic'))
        }
        return ok
      })
    }

    setBasketUnits(current => current.filter(bu => bu.unit.id !== unit.id))
    _setBasketAddedMessage(undefined)
    return true
  }, [_removeFromDb, basketUnits, isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated) {
      window.localStorage.setItem(basketLocalStorageKey, JSON.stringify(basketUnits))
    }
  }, [basketUnits, isAuthenticated])

  useEffect(() => {
    if (currentUser && !_basketInitiated) {
      setBasketUnits(currentUser?.basket)
      _setBasketInitiated(true)
    }
  }, [_basketInitiated, currentUser])

  useEffect(() => {
    if (!_basketInitiated) return

    const ls = _localStorageItems()
    if (ls.length === 0) return

    let cancelled = false

    const migrate = async () => {
      const results = await Promise.all(
        ls.map((container) => {
          if (!container.unit) return true

          const quantity = container.quantity ?? 1
          const mode = container.mode ?? 'payment'

          return Promise.resolve(addUnitToBasket(container.unit, false, quantity, mode))
        })
      )

      if (cancelled) return

      if (results.every(Boolean)) {
        window.localStorage.removeItem(basketLocalStorageKey)
        setShowBasket(true)
      }
    }

    void migrate()

    return () => {
      cancelled = true
    }
  }, [_basketInitiated, _localStorageItems, addUnitToBasket, setShowBasket])

  const [referral, _setReferral] = useState<string>(getLocalReferral())

  const setReferral = useCallback((value: string) => {
    _setReferral(value)
    setLocalReferral(value)
  }, [])

  return (
    <BasketContext.Provider value={ { basketUnits, addUnitToBasket, removeUnitFromBasket, clearBasket, setShowBasket, showBasket, showOrderAddress, setShowOrderAddress, setPreparedOrder, showOrderLoading, showOrderError, closeOrderError, showOrderComplete, basketAddedMessage, referral, setReferral, setShowOrderLoading } }>
      { children }
    </BasketContext.Provider>
  )
}

export { BasketProvider, BasketContext }
